import React, { ReactNode } from 'react';
import { Radio, Table, TableHeaderCellProps } from 'semantic-ui-react';
import { SortState } from '../..';
import { SortDirection } from '../../enums/SortDirection';
import { ColumnDefinition } from '../../interfaces/ColumnDefinition';
import POSDeselectCheckButton from '../POSDeselectCheckBox';

interface Props<TData> {
  columnDefinitions: ColumnDefinition<TData, any, any>[];
  handleSort: (clickedColumn: ColumnDefinition<TData, any, any>) => void;
  onCheckToggle?: (checked: boolean) => void;
  showCheckButton?: 'box' | 'radio' | boolean;
  showActionColumn?: boolean;
  allChecked?: boolean;
  sortState?: SortState<TData, any>;
  singleSelect?: boolean;
  checkedItemsLength?: number;
  showArrowForRowWithSubItems?: boolean;
}

type HeaderGroupCellDefinition = {
  key: string;
  props: TableHeaderCellProps;
  render?: () => ReactNode;
  headerCell?: (props: TableHeaderCellProps) => ReactNode;
};

interface TableHeaderProps extends TableHeaderCellProps {
  sortable?: boolean;
}

const SelectColumnHeader = <TData,>(props: Props<TData> & TableHeaderProps) => {
  let checkedSortDirection: 'ascending' | 'descending' | undefined;
  if ('CHECKED' == props.sortState?.name) {
    checkedSortDirection =
      props.sortState?.direction == SortDirection.Ascending
        ? 'ascending'
        : 'descending';
  }
  return props.singleSelect ? (
    <Table.HeaderCell
      sorted={checkedSortDirection ? checkedSortDirection : undefined}
      className={props.sortable ? 'sorted' : ''}
      onClick={() => {
        props.onCheckToggle?.(false);
      }}
    />
  ) : (
    <Table.HeaderCell key={'CHECKED'}>
      {props.showCheckButton === 'radio' ? (
        <Radio
          checked={props.allChecked ?? false}
          onClick={() => props.onCheckToggle?.(!props.allChecked)}
        />
      ) : (
        <POSDeselectCheckButton
          checkedItemsLength={!props.allChecked ? props.checkedItemsLength : 0}
          checked={props.allChecked ?? false}
          onToggle={(value: boolean) => props.onCheckToggle?.(value)}
        />
      )}
    </Table.HeaderCell>
  );
};

const getHeaderProps = <TData,>(
  columnDefinition: ColumnDefinition<TData, any, any>,
  handleSort: (clickedColumn: ColumnDefinition<TData, any, any>) => void,
  sortState?: SortState<TData, any>
) => {
  let sortDirection: 'ascending' | 'descending' | undefined;
  if (columnDefinition.name === sortState?.name) {
    sortDirection =
      sortState?.direction == SortDirection.Ascending
        ? 'ascending'
        : 'descending';
  }

  return {
    sorted: sortDirection,
    className: columnDefinition.sortable ? 'sorted' : undefined,
    key: columnDefinition.name,
    sortable: columnDefinition.sortable,
    onClick: () => {
      handleSort(columnDefinition);
    },
    width: columnDefinition.width,
  };
};

export const TableHeader = <TData,>(props: Props<TData>) => {
  const mainGroup: HeaderGroupCellDefinition[] = [];
  const secondGroup: HeaderGroupCellDefinition[] = [];
  const hasMultipleGroups =
    props.columnDefinitions.filter((x) => x.headerGroup).length > 0;

  if (props.showArrowForRowWithSubItems) {
    mainGroup.push({
      key: 'ARROW_SUBITEMS',
      props: {
        rowSpan: hasMultipleGroups ? 2 : 1,
      },
      headerCell: (headerCellProps: any) => (
        <Table.HeaderCell key={'ARROW_SUBITEMS'} {...headerCellProps} />
      ),
    });
  }

  if (props.showCheckButton) {
    mainGroup.push({
      key: 'CHECK',
      props: {
        rowSpan: hasMultipleGroups ? 2 : 1,
      },
      headerCell: (headerCellProps: any) => (
        <SelectColumnHeader key={'CHECKED'} {...props} {...headerCellProps} />
      ),
    });
  }

  props.columnDefinitions.forEach((x) => {
    if (x.headerGroup) {
      const headerGroup: any = mainGroup.find((y) => y.key === x.headerGroup);
      if (headerGroup) {
        headerGroup.props.colSpan += 1;
      } else {
        mainGroup.push({
          key: x.headerGroup,
          props: { colSpan: 1 },
          render: () => x.headerGroup,
        });
      }

      secondGroup.push({
        key: x.name,
        render: () => <>{x.name + ' '}</>,
        props: getHeaderProps(x, props.handleSort, props.sortState),
      });
    } else {
      mainGroup.push({
        key: x.name,
        render: () => <>{x.renderDropdown?.() ?? x.name}</>,
        props: {
          ...getHeaderProps(x, props.handleSort, props.sortState),
          rowSpan: hasMultipleGroups ? 2 : 1,
        },
      });
    }
  });

  const headerGroups = [{ key: 'th_row_1', headers: mainGroup }];
  if (secondGroup.length > 0) {
    headerGroups.push({ key: 'th_row_2', headers: secondGroup });
  }

  const renderHeaderCell = (column: HeaderGroupCellDefinition) => {
    if (column.headerCell) {
      return column.headerCell(column.props);
    }
    const { sortable, onClick, ...allOtherColumnProps } = column.props;
    return (
      <Table.HeaderCell
        key={column.key}
        sortable={sortable ? sortable?.toString() : 'false'}
        onClick={sortable ? onClick : () => null}
        {...allOtherColumnProps}
      >
        {column.render?.()}
      </Table.HeaderCell>
    );
  };

  return (
    <Table.Header>
      {headerGroups.map((headerGroup: any) => (
        <Table.Row key={headerGroup.key}>
          {headerGroup.headers.map((column: any) => {
            return renderHeaderCell(column);
          })}
        </Table.Row>
      ))}
    </Table.Header>
  );
};
